import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Fade from "@mui/material/Fade";
import Slide from "@mui/material/Slide";
import Grow from "@mui/material/Grow";
import Zoom from "@mui/material/Zoom";
import { useTranslation } from "react-i18next";
import { Dialog, DialogTitle, DialogContent, IconButton, FormGroup, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LivisCustomSwitch } from "../Switch/LivisSwitch";
import { toggleIncludeAnnotationModal } from "../../store/actions";
import { useDispatch } from "react-redux";
import { TransitionProps } from "@mui/material/transitions";
import LivisButton from "../Form/Button/LivisButton";


// Transition components mapped for Dialog. Defining at module scope ensures
// stable identities across renders, preventing flicker.
export const TransitionSlideUp = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export const TransitionSlideDown = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

export const TransitionSlideLeft = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="left" ref={ref} {...props} />;
});

export const TransitionSlideRight = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="right" ref={ref} {...props} />;
});

export const TransitionGrow = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Grow ref={ref} {...props} />;
});

export const TransitionFade = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Fade ref={ref} {...props} />;
});

export const TransitionZoom = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Zoom ref={ref} {...props} />;
});


const LivisModal = (props: any) => {
  const {
    children,
    size,
    modal_open,
    livis_dismiss_btn,
    disable_outside_click,
    animation_type,
    title,
    cancel_icon,
    livis_modal_class,
    sub_title,
    // When true, renders sub_title on its own line below the title instead
    // of inline next to it — for callers whose sub_title runs long enough
    // (e.g. a timestamp) that inline placement reads as cramped.
    stack_subtitle,
    is_from_augmentation,
    main_class,
    setToggleActive,
    toggleActive,
    setShowModal,
    subscription_sub_title,
    bodyHeight,
    isFullScreen,
    minHeight,
    footerPrimaryButton,
    footerSecondaryButton,
    // Arbitrary footer content (e.g. a selection count + toggle + button
    // group) rendered as a sticky footer outside the scrollable body, in
    // place of the footerPrimaryButton/footerSecondaryButton pair. Takes
    // precedence over those when provided.
    footerContent,
    forceAlignButton = false,
    contentClassName = "",
    fixedBodyHeight = false,
    // Optional exact-width override (e.g. "43rem") for callers whose design
    // doesn't match one of the fixed `size` presets. Takes precedence over
    // whatever `size` computes.
    customMaxWidth,
    // Extra class for the Paper/Backdrop specifically (NOT the fixed
    // full-viewport Dialog root — that's `livis_modal_class`, which many
    // existing callers already pass with legacy `width` overrides meant for
    // the visible box; applying those to the root breaks full-screen
    // centering). Use these two instead for new one-off modal styling.
    paperClassName = "",
    backdropClassName = "",
  } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [ maxWidth, setMaxWidth ] = useState<string | undefined>();
  const [ maxPaperHeight, setMaxPaperHeight ] = useState<string | undefined>();

  // GeneralModal style mapping
  const [modalStyle, setModalStyle] = useState("");
  useEffect(() => {
    switch (size) {
        case "small":
          setModalStyle("livis-general-modal-dialog livis-general-modal-fixed");
          setMaxWidth("30rem");
          setMaxPaperHeight(undefined);
          break;
        case "medium":
          setModalStyle("livis-general-modal-dialog livis-general-modal-fixed");
          setMaxWidth("70rem");
          setMaxPaperHeight(undefined);
          break;
        case "large":
          setModalStyle("livis-general-modal-dialog livis-general-modal-fixed");
          setMaxWidth("80rem");
          setMaxPaperHeight(undefined);
          break;
        case "xlarge":
          setModalStyle("livis-general-modal-dialog livis-general-modal-fixed");
          setMaxWidth("110rem");
          setMaxPaperHeight(undefined);
          break;
        case "full":
          setModalStyle("livis-general-modal-dialog livis-general-modal-fixed livis-general-modal-almost-full");
          setMaxWidth("97vw");
          setMaxPaperHeight("97vh");
          break;
      default:
        setModalStyle("livis-general-modal-dialog livis-general-modal-fixed");
        setMaxPaperHeight(undefined);
    }
    if (customMaxWidth) setMaxWidth(customMaxWidth);
  }, [size, customMaxWidth]);

  const handleCheck = () => {
    setToggleActive(!toggleActive);
    setShowModal(false);
    if (toggleActive) {
      dispatch(toggleIncludeAnnotationModal(false, false, !toggleActive));
    }
  };

  // Transition components are defined at module scope

  // Header content
  const renderHeader = () => (
    <DialogTitle
      id="general-modal-title"
      className="livis-general-modal-header"
    >
        <div className={'livis-general-modal-header-wrap'}>
      <div className="livis-general-modal-title-header">
        {stack_subtitle ? (
          <>
            <span className="livis-general-modal-title">{t(title)}</span>
            <span className="livis-general-modal-sub-title livis-general-modal-sub-title-stacked">{sub_title && t(sub_title)}</span>
          </>
        ) : (
          <span className="livis-general-modal-title">{t(title) } <span className="livis-general-modal-sub-title">{sub_title && t(sub_title)}</span></span>
        )}
        {title === "Include Annotations" && (
          <span style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <FormGroup>
              <Stack direction="row" spacing={1} alignItems="center" style={{ width: "70px" }}>
                <LivisCustomSwitch
                  id="switch"
                  name="include-annotations-switch"
                  checked={toggleActive}
                  onChange={handleCheck}
                  inputProps={{ "aria-label": "livis custom switch" }}
                />
                <Typography style={{ display: "flex", paddingInline: "2px", gap: "6px", fontFamily: "Noto Sans bold", fontSize: "var(--livis-font-size-xs)", lineHeight: "20px", letterSpacing: "0px", color: "#292931" }}>
                  {toggleActive ? t("Yes") : t("No")}
                </Typography>
              </Stack>
            </FormGroup>
          </span>
        )}
        {subscription_sub_title && <p className="livis-general-modal-sub-title">{t(subscription_sub_title)}</p>}
      </div>
      {/* <div className="livis-general-modal-sub-title-wrap">
      <span className="livis-general-modal-sub-title">{sub_title}</span>
      </div> */}
        </div>
        <div>
      {cancel_icon && (
        <IconButton
          aria-label="close"
          onClick={livis_dismiss_btn}
          className="livis-general-modal-close-btn"
          size="small"
        >
          <CloseIcon fontSize="small" className="livis-general-modal-close-icon" />
        </IconButton>
      )}
      </div>
    </DialogTitle>
  );

  // Body content
  const renderBody = () => (
    <DialogContent className={`livis-general-modal-content `} style={{ minHeight: minHeight, overflowY: "auto", scrollbarGutter: "stable", flex: 1, padding: "0 0", maxHeight: bodyHeight || (isFullScreen ? "100vh" : "71vh"), ...(fixedBodyHeight && bodyHeight ? { height: bodyHeight } : {}) }}>
      <Box className={`livis-general-modal-container ${main_class} ${is_from_augmentation ? "livis-augmentation-modal-main" : ""}`}>{children}</Box>
    </DialogContent>
  );

  // Footer content (if needed)
  const renderFooter = () => {
    if (footerContent) {
      return (
        <Box className="livis-general-modal-footer livis-general-modal-footer-custom">
          {footerContent}
        </Box>
      );
    }
    const hasFooter = footerPrimaryButton || footerSecondaryButton;
    if (!hasFooter) return null;
    return (
      <Box className="livis-general-modal-footer" sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, padding: "0.75rem 1rem" }}>
        {footerSecondaryButton && (
          <LivisButton
            label={t(footerSecondaryButton.label || "")}
            livis_click_function={footerSecondaryButton.onClick}
            livis_class={footerSecondaryButton.className || ""}
            icon={footerSecondaryButton.icon}
            icon_width={footerSecondaryButton.iconWidth}
            disabled={footerSecondaryButton.disabled}
            forceAlignButton={forceAlignButton}
          />
        )}
        {footerPrimaryButton && (
          <LivisButton
            label={t(footerPrimaryButton.label || "")}
            livis_click_function={footerPrimaryButton.onClick}
            livis_class={footerPrimaryButton.className || ""}
            icon={footerPrimaryButton.icon}
            icon_width={footerPrimaryButton.iconWidth}
            disabled={footerPrimaryButton.disabled}
            forceAlignButton={forceAlignButton}
          />
        )}
      </Box>
    );
  };

  // Choose Transition for Dialog based on animation_type, memoized
  const TransitionComponent = React.useMemo(() => {
    switch (animation_type) {
      case "none":
        return undefined;
      case "fade":
        return TransitionFade;
      case "grow":
        return TransitionGrow;
      case "slideup":
        return TransitionSlideUp;
      case "slidedown":
        return TransitionSlideDown;
      case "slideright":
        return TransitionSlideRight;
      case "slideleft":
        return TransitionSlideLeft;
      case "zoom":
        return TransitionZoom;
      default:
        return TransitionSlideDown;
    }
  }, [animation_type]);

  const transitionDuration = React.useMemo(() => (animation_type === "none" ? 0 : undefined), [animation_type]);

  return (
    <Dialog
      open={modal_open}
      onClose={disable_outside_click ? undefined : livis_dismiss_btn}
      className={modalStyle}
      aria-labelledby="general-modal-title"
      PaperProps={{
        className: `livis-general-modal-paper ${paperClassName}`,
        style: {
          width: maxWidth,
          maxWidth: "100%",
          ...(maxPaperHeight
            ? { maxHeight: maxPaperHeight, margin: "1.5vh 1.5vw", display: "flex", flexDirection: "column" }
            : {}),
        },
      }}
      BackdropProps={{ className: `livis-general-modal-backdrop ${backdropClassName}` }}
      disableEnforceFocus
      TransitionComponent={TransitionComponent}
      transitionDuration={transitionDuration}

      
    >
      <Box className={`livis-general-modal-container ${contentClassName}`}>
        {renderHeader()}
        {renderBody()}
        {renderFooter()}
      </Box>
    </Dialog>
  );
};

LivisModal.propTypes = {
  children: PropTypes.node,
  size: PropTypes.string.isRequired,
  modal_open: PropTypes.bool,
  livis_dismiss_btn: PropTypes.func,
  disable_outside_click: PropTypes.bool,
  animation_type: PropTypes.string,
  title: PropTypes.string,
  cancel_icon: PropTypes.bool,
  livis_modal_class: PropTypes.string,
  sub_title: PropTypes.string,
  stack_subtitle: PropTypes.bool,
  is_from_augmentation: PropTypes.bool,
  main_class: PropTypes.string,
  subscription_sub_title: PropTypes.string,
  setToggleActive: PropTypes.func,
  toggleActive: PropTypes.bool,
  setShowModal: PropTypes.func,
  bodyHeight: PropTypes.string,
  isFullScreen: PropTypes.bool,
  minHeight: PropTypes.string,
  contentClassName: PropTypes.string,
  fixedBodyHeight: PropTypes.bool,
  customMaxWidth: PropTypes.string,
  paperClassName: PropTypes.string,
  backdropClassName: PropTypes.string,
  footerPrimaryButton: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func,
    className: PropTypes.string,
    icon: PropTypes.string,
    iconWidth: PropTypes.number,
    disabled: PropTypes.bool,
  }),
  footerSecondaryButton: PropTypes.shape({
    label: PropTypes.string,
    onClick: PropTypes.func,
    className: PropTypes.string,
    icon: PropTypes.string,
    iconWidth: PropTypes.number,
    disabled: PropTypes.bool,
  }),
  footerContent: PropTypes.node,
  forceAlignButton: PropTypes.bool,
};

LivisModal.defaultProps = {
  children: <></>,
  modal_open: false,
  livis_dismiss_btn: () => { return null; },
  disable_outside_click: false,
  animation_type: "none",
  title: "",
  cancel_icon: false,
  livis_modal_class: "",
  sub_title: "",
  stack_subtitle: false,
  is_from_augmentation: false,
  subscription_sub_title: "",
  isFullScreen: false,
  minHeight: "100px",
  contentClassName: "",
  fixedBodyHeight: false,
  customMaxWidth: undefined,
  paperClassName: "",
  backdropClassName: "",
  footerPrimaryButton: undefined,
  footerSecondaryButton: undefined,
  footerContent: undefined,
  forceAlignButton: false,
};

export default LivisModal;